// src/pages/Arcades.js

import React from "react";
import arcades from "../data/arcades";

const featuredMachines = [
  "Love Live! After School Idol Activity",
  "Final Fantasy Theatrhythm",
  "Dance Evolution",
  "WACCA Reverse",
  "DanceRush Stardom",
  "Sound Voltex Exceed Gear",
];

const imageBase = "/images/arcades";

const categories = [
  {
    id: "rhythm",
    title: "Rhythm Games",
    description:
      "Rare Japanese rhythm games and dance games that are seldom seen outside dedicated arcades.",
    machines: arcades.filter((a) => a.category === "Rhythm"),
  },
  {
    id: "fighting",
    title: "Fighting Games",
    description:
      "Head-to-head competitive games and versatile cabinets.",
    machines: arcades.filter((a) => a.category === "Fighting"),
  },
  {
    id: "prize",
    title: "Prize & Attractions",
    description:
      "Crane games, gacha machines, and prize attractions.",
    machines: arcades.filter((a) => a.category === "Prize"),
  },
  {
    id: "puzzle",
    title: "Puzzle Games",
    description:
      "Unique puzzle experiences and multiplayer challenges.",
    machines: arcades.filter((a) => a.category === "Puzzle"),
  },
  {
    id: "action",
    title: "Action Games",
    description:
      "Immersive arcade experiences.",
    machines: arcades.filter((a) => a.category === "Action"),
  },
];

const Arcades = () => {
  return (
    <main className="bg-background min-h-screen">

      {/* HERO */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-5xl">

          <span className="uppercase tracking-widest font-bold opacity-80">
            Free Play Arcade Rentals
          </span>

          <h1 className="text-5xl md:text-7xl font-display mt-4 mb-6">
            Rare Japanese Arcade Collection
          </h1>

          <p className="text-xl max-w-3xl mx-auto opacity-90">
            Featuring Love Live! After School Idol Activity, Final Fantasy
            Theatrhythm, Dance Evolution, WACCA Reverse, DanceRush Stardom,
            Sound Voltex Exceed Gear, Project DIVA, Pop'n Music HD and more.
          </p>

        </div>
      </section>

      {/* STATS */}
      <section className="py-16 border-b">
        <div className="container mx-auto px-4">

          <div className="grid md:grid-cols-3 gap-8 text-center">

            <div>
              <div className="text-5xl font-display text-primary">
                30
              </div>
              <p className="mt-2 font-medium">
                Arcade Machines
              </p>
            </div>

            <div>
              <div className="text-5xl font-display text-primary">
                62
              </div>
              <p className="mt-2 font-medium">
                Tomy Gacha Machines
              </p>
            </div>

            <div>
              <div className="text-5xl font-display text-primary">
                100%
              </div>
              <p className="mt-2 font-medium">
                Free Play
              </p>
            </div>

          </div>

        </div>
      </section>

      {/* FEATURED */}
      <section className="py-24">
        <div className="container mx-auto px-4">

          <div className="text-center mb-16">
            <h2 className="text-5xl font-display mb-4">
              Featured Attractions
            </h2>

            <p className="text-muted-foreground max-w-3xl mx-auto">
              Some of the most unusual and difficult-to-find machines
              in our collection.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">

            {arcades
              .filter((m) => featuredMachines.includes(m.name))
              .map((machine) => (
                <div
                  key={machine.name}
                  className="bg-card border rounded-3xl overflow-hidden shadow-sm"
                >

                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={`${imageBase}/${machine.image}`}
                      alt={machine.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-6">

                    <h3 className="text-2xl font-bold mb-3">
                      {machine.name}
                    </h3>

                    <div className="text-primary font-semibold">
                      {machine.count} Available
                    </div>

                    {machine.note && (
                      <p className="mt-4 text-muted-foreground">
                        {machine.note}
                      </p>
                    )}

                  </div>

                </div>
              ))}

          </div>

        </div>
      </section>

      {/* NAVIGATION */}
      <section className="pb-16">
        <div className="container mx-auto px-4">

          <div className="flex flex-wrap justify-center gap-4">

            <a href="#rhythm" className="px-6 py-3 rounded-xl bg-muted">
              Rhythm Games
            </a>

            <a href="#fighting" className="px-6 py-3 rounded-xl bg-muted">
              Fighting Games
            </a>

            <a href="#prize" className="px-6 py-3 rounded-xl bg-muted">
              Prize & Attractions
            </a>

            <a href="#puzzle" className="px-6 py-3 rounded-xl bg-muted">
              Puzzle Games
            </a>

            <a href="#action" className="px-6 py-3 rounded-xl bg-muted">
              Action Games
            </a>

          </div>

        </div>
      </section>

      {/* CATEGORY SECTIONS */}
      {categories.map((category) => (
        <section
          key={category.id}
          id={category.id}
          className="py-24"
        >
          <div className="container mx-auto px-4">

            <div className="mb-16 text-center">

              <h2 className="text-5xl font-display mb-4">
                {category.title}
              </h2>

              <p className="text-muted-foreground max-w-3xl mx-auto">
                {category.description}
              </p>

            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">

              {category.machines.map((machine) => (
                <div
                  key={machine.name}
                  className="bg-card border rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition"
                >

                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={`${imageBase}/${machine.image}`}
                      alt={machine.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="p-6">

                    <h3 className="text-xl font-bold mb-2">
                      {machine.name}
                    </h3>

                    <div className="text-primary font-semibold mb-4">
                      {machine.count} Available
                    </div>

                    {machine.note && (
                      <p className="text-sm text-muted-foreground">
                        {machine.note}
                      </p>
                    )}

                  </div>

                </div>
              ))}

            </div>

          </div>
        </section>
      ))}


            {/* GACHA MACHINES */}
      <section className="py-24 bg-muted">
        <div className="container mx-auto px-4">

          <div className="max-w-5xl mx-auto bg-background border rounded-3xl p-12">

            <div className="grid lg:grid-cols-2 gap-12 items-center">

              <div>

                <div className="text-primary font-bold uppercase tracking-widest mb-4">
                  Capsule Toy Machines
                </div>

                <h2 className="text-5xl font-display mb-6">
                  62 Tomy Gacha Machines
                </h2>

                <p className="text-lg text-muted-foreground mb-6">
                  In addition to our arcade collection, we maintain a fleet
                  of authentic Tomy capsule toy machines that can be used
                  for prize redemption, fundraising, scavenger hunts,
                  giveaways, or custom event experiences.
                </p>

                <p className="text-muted-foreground">
                  Gacha machines can help offset the cost of activities and
                  provide a unique attraction for attendees of all ages.
                </p>

              </div>

              <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                <img
                  src="/arcades/gacha.jpg"
                  alt="Tomy Gacha Machines"
                  className="w-full h-full object-cover"
                />
              </div>

            </div>

          </div>

        </div>
      </section>

            {/* EVENT SUPPORT */}
      <section className="py-24">
        <div className="container mx-auto px-4">

          <div className="text-center mb-16">

            <h2 className="text-5xl font-display mb-4">
              Event Support
            </h2>

            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our team handles transportation, setup, operation, and
              teardown so organizers can focus on creating memorable
              experiences for attendees.
            </p>

          </div>

          <div className="grid md:grid-cols-4 gap-8">

            <div className="bg-card border rounded-3xl p-8 text-center">
              <div className="text-4xl mb-4">
                🚚
              </div>

              <h3 className="font-bold mb-2">
                Delivery
              </h3>

              <p className="text-sm text-muted-foreground">
                Transportation and logistics available.
              </p>
            </div>

            <div className="bg-card border rounded-3xl p-8 text-center">
              <div className="text-4xl mb-4">
                🔧
              </div>

              <h3 className="font-bold mb-2">
                Setup
              </h3>

              <p className="text-sm text-muted-foreground">
                Equipment installation and configuration.
              </p>
            </div>

            <div className="bg-card border rounded-3xl p-8 text-center">
              <div className="text-4xl mb-4">
                🕹️
              </div>

              <h3 className="font-bold mb-2">
                Free Play
              </h3>

              <p className="text-sm text-muted-foreground">
                No coins, tokens, or tickets required.
              </p>
            </div>

            <div className="bg-card border rounded-3xl p-8 text-center">
              <div className="text-4xl mb-4">
                📦
              </div>

              <h3 className="font-bold mb-2">
                Teardown
              </h3>

              <p className="text-sm text-muted-foreground">
                Quick removal after the event concludes.
              </p>
            </div>

          </div>

        </div>
      </section>

    {/* CTA */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-4xl">

          <h2 className="text-5xl font-display mb-6">
            Bring These Attractions To Your Event
          </h2>

          <p className="text-xl opacity-90 mb-10">
            Whether you're organizing a convention, library event,
            festival, school function, or community gathering, we'd
            love to help create something memorable.
          </p>

          <a
            href="/event-support"
            className="inline-flex px-10 py-5 rounded-2xl bg-white text-primary font-bold text-lg hover:opacity-90 transition"
          >
            Request Event Support
          </a>

        </div>
      </section>

    </main>
  );
};

export default Arcades;